import "@/index.css";

import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ApiError } from "@/api";
import App from "@/App";

// Single shared QueryClient. TanStack Query handles polling, caching, and
// loading/error states for every server-state hook the pages add later;
// per the spec there is no global state library beyond useState +
// QueryClient. The defaults below match the dashboard spec's polling
// cadence (30s) for the detection list — individual hooks may override.
//
// The QueryCache's onError is the app's whole "am I logged in" mechanism:
// every authenticated page fires a useQuery on mount, so a session-less
// visit surfaces a 401 through that existing traffic rather than needing a
// dedicated session-check endpoint. This only wraps QueryCache, not
// MutationCache — LoginPage's own sign-in flow is a plain browser
// navigation (not a query or mutation), so a failed login attempt can
// never trigger this handler or its redirect.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) {
        window.location.assign("/login");
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("expected #root element to exist in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
